import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { listExceptions } from '../../api/originatorApi';
import { DataTable } from '../../components/ui/DataTable';
import { StatusPill } from '../../components/ui/StatusPill';
import { FilterSelect } from '../../components/ui/FilterSelect';
import { Pagination } from '../../components/ui/Pagination';
import { KpiCard } from '../../components/ui/KpiCard';
import { Button } from '../../components/ui/Button';
import { IconAlert, IconSearch } from '../../components/ui/icons';
import { CureExceptionModal } from '../../components/exceptions/CureExceptionModal';
import { FEATURES } from '../../config/features';

const STATUS_TABS = [
  { value: '', label: 'All' },
  { value: 'OPEN', label: 'Open' },
  { value: 'IN_CURE', label: 'In cure' },
  { value: 'CURED', label: 'Cured' },
];

const SEVERITY_TABS = [
  { value: '', label: 'All severity' },
  { value: 'CRITICAL', label: 'Critical' },
  { value: 'HIGH', label: 'High' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'LOW', label: 'Low' },
];

const OWNER_TABS = [
  { value: 'originator', label: 'Owner: you' },
  { value: '', label: 'Owner: all' },
  { value: 'other', label: 'Owner: assigned' },
];

const PAGE_SIZE_DEFAULT = 20;

export function ExceptionsPage() {
  const [severityFilter, setSeverityFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('OPEN');
  const [assigneeOrg, setAssigneeOrg] = useState('originator');
  const [searchValue, setSearchValue] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);
  const [cureTarget, setCureTarget] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function loadExceptions() {
      setLoading(true);
      setError('');
      try {
        const response = await listExceptions({
          page,
          pageSize,
          severity: severityFilter || undefined,
          status: statusFilter || undefined,
          assigneeOrg: assigneeOrg || undefined,
        });
        if (cancelled) return;
        setRows(response.items || []);
        setTotal(response.meta?.total || 0);
      } catch (requestError) {
        if (cancelled) return;
        setError(requestError.message || 'Unable to load exceptions.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadExceptions();
    return () => {
      cancelled = true;
    };
  }, [assigneeOrg, page, pageSize, severityFilter, statusFilter, reloadToken]);

  const visibleRows = useMemo(() => {
    const term = searchValue.trim().toLowerCase();
    const base = term
      ? rows.filter(
          (row) =>
            row.id.toLowerCase().includes(term) ||
            row.package_id.toLowerCase().includes(term) ||
            row.code.toLowerCase().includes(term) ||
            (row.description || '').toLowerCase().includes(term)
        )
      : rows;
    return base.map((row, index) => ({
      ...row,
      __sr: (page - 1) * pageSize + index + 1,
    }));
  }, [rows, searchValue, page, pageSize]);

  const columns = useMemo(
    () => [
      {
        key: 'sr',
        header: 'Sr',
        render: (row) => <span className="text-eorig-muted">{row.__sr}</span>,
      },
      {
        key: 'severity',
        header: 'Priority',
        render: (row) => <StatusPill value={row.severity} />,
      },
      {
        key: 'package_id',
        header: 'Package',
        render: (row) => (
          <span className="font-mono text-[11px] text-eorig-muted">
            {row.package_id}
          </span>
        ),
      },
      {
        key: 'code',
        header: 'Code',
        render: (row) => (
          <Link
            to={`/exceptions/${row.id}`}
            state={{ exception: row }}
            onClick={(e) => e.stopPropagation()}
            className="font-mono text-[12px] font-semibold text-eorig-navy-600 hover:text-eorig-cyan"
            title={row.code}
          >
            {row.code}
          </Link>
        ),
      },
      {
        key: 'description',
        header: 'What happened',
        className: 'max-w-[18rem]',
        render: (row) => (
          <span className="text-eorig-ink/80">
            {row.description || 'No description provided'}
            {row.fact_code ? ` · Fact ${row.fact_code}` : ''}
          </span>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        render: (row) => <StatusPill value={row.status} />,
      },
      {
        key: 'assigned_to',
        header: 'Owner',
        render: (row) => (
          <span className="text-eorig-ink/80">
            {row.assigned_to || 'Unassigned'}
          </span>
        ),
      },
      {
        key: 'action',
        header: '',
        align: 'right',
        render: (row) => (
          <Button
            variant="ghost"
            className="px-3 py-1 text-xs"
            disabled={row.status === 'CURED'}
            onClick={(e) => {
              e.stopPropagation();
              setCureTarget(row);
            }}
          >
            {row.status === 'CURED' ? 'Cured' : 'Cure'}
          </Button>
        ),
      },
    ],
    []
  );

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard
          label="Exceptions"
          value={total}
          subline="Matching current filters"
          icon={IconAlert}
          accent
          loading={loading}
        />
        {FEATURES.exceptionTypes ? (
          <>
            <KpiCard label="With custodian" value={0} loading={loading} />
            <KpiCard label="In progress" value={0} loading={loading} />
            <KpiCard label="Resolved" value={0} loading={loading} />
          </>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-[#E6EAF0] bg-white px-3 py-2">
        <FilterSelect
          label="Status"
          ariaLabel="Filter by status"
          options={STATUS_TABS}
          value={statusFilter}
          onChange={(value) => {
            setPage(1);
            setStatusFilter(value);
          }}
        />
        <FilterSelect
          label="Severity"
          ariaLabel="Filter by severity"
          options={SEVERITY_TABS}
          value={severityFilter}
          onChange={(value) => {
            setPage(1);
            setSeverityFilter(value);
          }}
        />
        <FilterSelect
          label="Owner"
          ariaLabel="Filter by owner"
          options={OWNER_TABS}
          value={assigneeOrg}
          onChange={(value) => {
            setPage(1);
            setAssigneeOrg(value);
          }}
        />
        <div className="relative min-w-[14rem] flex-1">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#5A5A5A]">
            <IconSearch className="h-4 w-4" />
          </span>
          <input
            type="search"
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            placeholder="Search this page by exception, package, or code"
            aria-label="Search exceptions"
            className="h-9 w-full rounded-md border border-[#D1D5DB] bg-white py-0 pl-9 pr-3 text-sm text-[#1F2933] placeholder:text-[#8D8D8D] focus:border-[#00A6D6] focus:outline-none focus:ring-2 focus:ring-[#00A6D6]/30"
          />
        </div>
        {FEATURES.bulkActions ? (
          <Button variant="ghost">Bulk solve</Button>
        ) : null}
        {FEATURES.listExports ? (
          <Button variant="ghost">Export</Button>
        ) : null}
      </div>

      {error ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          {error}
        </div>
      ) : null}

      <DataTable
        columns={columns}
        rows={visibleRows}
        rowKey={(row) => row.id}
        rowHref={(row) => `/exceptions/${row.id}`}
        loading={loading}
        emptyMessage="No exceptions found for selected filters."
        minWidth={900}
      />

      <Pagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPage(1);
          setPageSize(size);
        }}
        disabled={loading}
      />

      <CureExceptionModal
        exception={cureTarget}
        onClose={() => setCureTarget(null)}
        onCured={() => {
          setCureTarget(null);
          setReloadToken((t) => t + 1);
        }}
      />
    </div>
  );
}
